// ====================================================================
// CLIENT FOLLOW-UP CRM - CLIENT PROFILE & INTERACTION TIMELINE
// ====================================================================

import { getClientById, getInteractions, deleteInteraction } from '../store.js';
import { isAdmin } from '../auth.js';
import { showToast, escapeHtml, formatDate } from '../ui.js';
import { openAddInteractionModal } from './staff_view.js';
import { openClientModal } from './clients.js';

export async function renderProfileView(container, clientId) {
  if (!clientId) {
    container.innerHTML = `<div class="card" style="text-align:center; padding:3rem;"><h3 style="color:var(--text-muted);">No client selected.</h3><a href="#clients" class="btn btn-primary" style="margin-top:1rem;">Go to Client Directory</a></div>`;
    return;
  }

  const client = await getClientById(clientId);
  if (!client) {
    container.innerHTML = `<div class="card" style="text-align:center; padding:3rem;"><h3 style="color:var(--text-muted);">Client not found.</h3><a href="#clients" class="btn btn-primary" style="margin-top:1rem;">Go to Client Directory</a></div>`;
    return;
  }

  const interactions = await getInteractions(clientId);

  const isUserAdmin = isAdmin();

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex; align-items:center; gap:1rem;">
        <a href="#clients" class="btn btn-secondary btn-sm">
          ← Back
        </a>
        <div>
          <h1 class="page-title">${escapeHtml(client.client_name)}</h1>
          <p class="page-subtitle">${escapeHtml(client.company || 'No Company')} &bull; Created ${formatDate(client.created_at)}</p>
        </div>
      </div>
      <div style="display:flex; gap:0.75rem;">
        <button id="btn-edit-client-profile" class="btn btn-secondary">Edit Client</button>
        <button id="btn-add-interaction-profile" class="btn btn-primary">+ Add Interaction</button>
      </div>
    </div>

    <div class="profile-grid">
      <!-- Left Column: Details Card -->
      <div>
        <div class="card">
          <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:1rem;">
            <span class="badge badge-${(client.category || 'warm').toLowerCase()}" style="font-size:0.875rem; padding:0.35rem 0.85rem;">
              ${escapeHtml(client.category || 'Warm')} Lead
            </span>
            <span style="font-size:0.8125rem; color:var(--text-muted);">ID: ${escapeHtml(client.id)}</span>
          </div>

          <div style="display:flex; flex-direction:column; gap:1rem; margin-top:1rem; font-size:0.9rem;">
            <div>
              <div style="font-size:0.75rem; color:var(--text-muted); font-weight:600; text-transform:uppercase;">Mobile Number</div>
              <div style="font-weight:700; font-size:1.1rem; color:var(--primary); margin-top:0.1rem;">${escapeHtml(client.mobile_number)}</div>
            </div>

            ${client.alternate_mobile ? `
              <div>
                <div style="font-size:0.75rem; color:var(--text-muted); font-weight:600; text-transform:uppercase;">Alternate Mobile</div>
                <div>${escapeHtml(client.alternate_mobile)}</div>
              </div>
            ` : ''}

            ${client.email ? `
              <div>
                <div style="font-size:0.75rem; color:var(--text-muted); font-weight:600; text-transform:uppercase;">Email Address</div>
                <div><a href="mailto:${escapeHtml(client.email)}" style="color:var(--primary);">${escapeHtml(client.email)}</a></div>
              </div>
            ` : ''}

            <div>
              <div style="font-size:0.75rem; color:var(--text-muted); font-weight:600; text-transform:uppercase;">Company</div>
              <div>${escapeHtml(client.company || 'Not Specified')}</div>
            </div>

            <div>
              <div style="font-size:0.75rem; color:var(--text-muted); font-weight:600; text-transform:uppercase;">Assigned Owner</div>
              <div style="font-weight:600;">${escapeHtml(client.owner?.name || 'Unassigned')}</div>
            </div>

            ${client.address ? `
              <div>
                <div style="font-size:0.75rem; color:var(--text-muted); font-weight:600; text-transform:uppercase;">Address</div>
                <div style="color:var(--text-secondary); line-height:1.4;">${escapeHtml(client.address)}</div>
              </div>
            ` : ''}
          </div>
        </div>
      </div>

      <!-- Right Column: Interaction Timeline -->
      <div>
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Interaction History Timeline</h3>
            <span class="badge badge-upcoming">${interactions.length} Logged</span>
          </div>

          ${interactions.length === 0 ? `
            <div style="text-align:center; padding:2.5rem 1rem; color:var(--text-muted);">
              <svg width="40" height="40" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="margin-bottom:0.5rem; opacity:0.4;"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
              <p>No interactions recorded yet.</p>
              <button id="btn-timeline-first-int" class="btn btn-primary btn-sm" style="margin-top:0.75rem;">Log First Interaction</button>
            </div>
          ` : `
            <div class="timeline">
              ${interactions.map(i => `
                <div class="timeline-item">
                  <div class="timeline-card">
                    <div class="timeline-meta">
                      <span class="timeline-date">${formatDate(i.interaction_date)} &bull; ${escapeHtml(i.creator?.name || 'Staff')}</span>
                      <div style="display:flex; align-items:center; gap:0.5rem;">
                        <span class="badge badge-warm">${escapeHtml(i.status)}</span>
                        ${isUserAdmin ? `<button class="modal-close btn-del-int" data-id="${i.id}" title="Delete interaction">&times;</button>` : ''}
                      </div>
                    </div>

                    ${i.next_followup_date ? `
                      <div style="margin-top:0.25rem;">
                        <span style="font-size:0.78rem; font-weight:600; color:var(--text-muted);">Next Follow-up:</span>
                        <span class="badge badge-today" style="font-size:0.7rem;">${formatDate(i.next_followup_date)}</span>
                      </div>
                    ` : ''}

                    <div class="timeline-notes">${escapeHtml(i.notes || 'No detailed notes logged.')}</div>
                  </div>
                </div>
              `).join('')}
            </div>
          `}
        </div>
      </div>
    </div>
  `;

  // Attach button listeners
  document.getElementById('btn-add-interaction-profile').addEventListener('click', () => {
    openAddInteractionModal(client.id, client.client_name);
  });

  document.getElementById('btn-timeline-first-int')?.addEventListener('click', () => {
    openAddInteractionModal(client.id, client.client_name);
  });

  document.getElementById('btn-edit-client-profile').addEventListener('click', () => {
    openClientModal(client);
  });

  if (isUserAdmin) {
    document.querySelectorAll('.btn-del-int').forEach(b => {
      b.addEventListener('click', async (e) => {
        const id = e.currentTarget.dataset.id;
        if (confirm('Delete this interaction log?')) {
          try {
            await deleteInteraction(id);
            showToast('Interaction deleted', 'success');
            renderProfileView(container, clientId);
          } catch (err) {
            showToast(err.message || 'Failed to delete interaction', 'error');
          }
        }
      });
    });
  }
}
